import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosPost from '../helpers/axios-helper';
import '../styles/Blog.css';

export default function QueerLandscape() {
    const { countryId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [queerLandscape, setQueerLandscape] = useState(null);
    const [countryName, setCountryName] = useState('');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const closeLightbox = () => setLightboxOpen(false);
    const showPrev = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
    const showNext = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

    // Keyboard navigation for lightbox
    useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') showPrev();
            if (e.key === 'ArrowRight') showNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxOpen, lightboxImages.length]);

    const formatDate = (unix) => {
        if (!unix) return '';
        try { return new Date(unix * 1000).toLocaleDateString(); } catch { return ''; }
    };

    // Render blocks exactly like Blog.js
    const renderBlocks = (blocks) => {
        if (!Array.isArray(blocks)) return null;
        return blocks.map((b, idx) => {
            if (b.type === 'header') {
                return (
                    <h2 key={idx} className="blog-block-header">
                        {b.content}
                    </h2>
                );
            }
            if (b.type === 'image') {
                const c = b.content;
                if (typeof c === 'string') {
                    return (
                        <div key={idx} className="blog-image-item">
                            <img
                                src={c}
                                alt=""
                                className="blog-block-image blog-image-clickable"
                                onClick={() => {
                                    setLightboxImages([c]);
                                    setLightboxIndex(0);
                                    setLightboxOpen(true);
                                }}
                            />
                        </div>
                    );
                }
                if (c && Array.isArray(c.images)) {
                    const images = c.images;
                    const Wrapper = ({ children }) => (
                        <div className={images.length > 1 ? 'blog-images-grid' : ''}>{children}</div>
                    );
                    return (
                        <Wrapper key={idx}>
                            {images.map((im, i) => (
                                <div key={`img-${idx}-${i}`} className="blog-image-item">
                                    <img
                                        src={im.url}
                                        alt={im.caption || ''}
                                        className="blog-block-image blog-image-clickable"
                                        onClick={() => {
                                            const urls = images.map(m => m.url);
                                            setLightboxImages(urls);
                                            setLightboxIndex(i);
                                            setLightboxOpen(true);
                                        }}
                                    />
                                    {im.caption && (
                                        <div className="blog-image-caption">{im.caption}</div>
                                    )}
                                </div>
                            ))}
                        </Wrapper>
                    );
                }
                return null;
            }
            if (b.type === 'video') {
                const c = b.content;
                if (c && c.url) {
                    const formatVideoUrl = (url) => {
                        if (url.includes('youtube.com/watch')) {
                            const videoId = new URL(url).searchParams.get('v');
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('youtu.be')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('vimeo.com')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://player.vimeo.com/video/${videoId}`;
                        }
                        return url;
                    };
                    return (
                        <div key={idx} className="blog-video-container">
                            <iframe
                                src={formatVideoUrl(c.url)}
                                title={c.caption || 'Video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                            {c.caption && (
                                <div className="blog-image-caption">{c.caption}</div>
                            )}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'text') {
                const c = b.content;
                try {
                    if (typeof c === 'string') {
                        // Check if it's HTML content
                        if (c.startsWith('<') || c.includes('<p>') || c.includes('<h')) {
                            return (
                                <div 
                                    key={idx} 
                                    className="blog-block-html"
                                    dangerouslySetInnerHTML={{ __html: c }}
                                />
                            );
                        }
                        // Try to parse as Draft.js JSON for backward compatibility
                        if (c.startsWith('{')) {
                            const raw = JSON.parse(c);
                            if (raw && Array.isArray(raw.blocks)) {
                                return (
                                    <div key={idx}>
                                        {raw.blocks.map((blk, bi) => {
                                            switch (blk.type) {
                                                case 'header-one':
                                                    return <h1 key={`h1-${idx}-${bi}`} className="blog-block-h1">{blk.text}</h1>;
                                                case 'header-two':
                                                    return <h2 key={`h2-${idx}-${bi}`} className="blog-block-h2">{blk.text}</h2>;
                                                case 'header-three':
                                                    return <h3 key={`h3-${idx}-${bi}`} className="blog-block-h3">{blk.text}</h3>;
                                                case 'blockquote':
                                                    return <blockquote key={`bq-${idx}-${bi}`} className="blog-block-blockquote">{blk.text}</blockquote>;
                                                default:
                                                    return <p key={`p-${idx}-${bi}`} className="blog-block-paragraph">{blk.text}</p>;
                                            }
                                        })}
                                    </div>
                                );
                            }
                        }
                        // Plain text
                        return (
                            <p key={idx} className="blog-block-paragraph">
                                {c}
                            </p>
                        );
                    }
                } catch (_) {
                    // Fallback to plain paragraph if not JSON
                }
                return (
                    <p key={idx} className="blog-block-paragraph">
                        {typeof c === 'string' ? c : ''}
                    </p>
                );
            }
            return null;
        });
    };

    // Check if we have blocks (new format) or legacy fields
    const hasBlocks = queerLandscape?.blocks && Array.isArray(queerLandscape.blocks) && queerLandscape.blocks.length > 0;
    const hasLegacyContent = queerLandscape?.history_visibility || queerLandscape?.legal_landscape || 
                            queerLandscape?.lived_experience || queerLandscape?.cultural_religious ||
                            queerLandscape?.community_activism || queerLandscape?.traveler_info ||
                            queerLandscape?.safety_snapshot || queerLandscape?.queer_joy || 
                            queerLandscape?.final_perspective;

    useEffect(() => {
        if (!countryId) {
            navigate('/');
            return;
        }

        // Fetch country info
        axiosPost(
            'countries/get',
            { 'Content-Type': 'application/json' },
            { id: parseInt(countryId) },
            (resp) => {
                if (resp?.success && resp?.data && resp.data.length > 0) {
                    setCountryName(resp.data[0].name || '');
                }
            }
        );

        // Fetch Queer Landscape content
        axiosPost(
            'queer-landscape/get',
            { 'Content-Type': 'application/json' },
            { country_id: parseInt(countryId) },
            (data) => {
                if (data?.success && data?.data) {
                    setQueerLandscape(data.data);
                } else {
                    setQueerLandscape(null);
                }
                setLoading(false);
            },
            (err) => {
                console.error('Failed to load Queer Landscape content:', err);
                setQueerLandscape(null);
                setLoading(false);
            }
        );
    }, [countryId, navigate]);

    if (loading) return <div className="blog-loading">Loading...</div>;
    if (!queerLandscape) return <div className="blog-not-found">Content not available for this destination.</div>;

    // Use title from new format or generate from country name
    const displayTitle = queerLandscape.title || `The Queer Landscape in ${countryName}`;
    const displayExcerpt = queerLandscape.excerpt || queerLandscape.intro_text;

    return (
        <div className="blog-container" style={{ paddingTop: 140}}>
            {/* Hero Image */}
            {queerLandscape.image && (
                <img
                    src={queerLandscape.image}
                    alt={displayTitle}
                    className="blog-hero"
                />
            )}

            {/* Title */}
            <h1 className="blog-title">{displayTitle}</h1>

            {/* Meta */}
            <div className="blog-meta">
                <span className="blog-category">Queer Landscape</span>
                <span className="blog-meta-sep">|</span>
                <span>{countryName}</span>
                {queerLandscape.updated_at && (
                    <>
                        <span className="blog-meta-sep">|</span>
                        <span>{formatDate(queerLandscape.updated_at)}</span>
                    </>
                )}
            </div>

            {/* Excerpt */}
            {displayExcerpt && (
                <div className="blog-excerpt">{displayExcerpt}</div>
            )}

            {/* Content / Blocks */}
            <div className="blog-blocks">
                {/* Render blocks if available (new format) */}
                {hasBlocks && renderBlocks(queerLandscape.blocks)}

                {/* Fallback to legacy content if no blocks */}
                {!hasBlocks && hasLegacyContent && (
                    <>
                        {queerLandscape.history_visibility && (
                            <>
                                <h2 className="blog-block-h2">1. Queer History & Visibility</h2>
                                <p className="blog-block-paragraph">{queerLandscape.history_visibility}</p>
                            </>
                        )}

                        {queerLandscape.legal_landscape && (
                            <>
                                <h2 className="blog-block-h2">2. The Legal Landscape (Law vs Lived Reality)</h2>
                                <p className="blog-block-paragraph">{queerLandscape.legal_landscape}</p>
                            </>
                        )}

                        {queerLandscape.lived_experience && (
                            <>
                                <h2 className="blog-block-h2">3. Everyday Lived Experience</h2>
                                <p className="blog-block-paragraph">{queerLandscape.lived_experience}</p>
                            </>
                        )}

                        {queerLandscape.cultural_religious && (
                            <>
                                <h2 className="blog-block-h2">4. Cultural & Religious Context</h2>
                                <p className="blog-block-paragraph">{queerLandscape.cultural_religious}</p>
                            </>
                        )}

                        {queerLandscape.community_activism && (
                            <>
                                <h2 className="blog-block-h2">5. Community, Activism & Visibility Today</h2>
                                <p className="blog-block-paragraph">{queerLandscape.community_activism}</p>
                            </>
                        )}

                        {queerLandscape.traveler_info && (
                            <>
                                <h2 className="blog-block-h2">6. What Queer Travelers Should Know Before Visiting</h2>
                                <p className="blog-block-paragraph">{queerLandscape.traveler_info}</p>
                            </>
                        )}

                        {queerLandscape.safety_snapshot && (
                            <>
                                <h2 className="blog-block-h2">7. Safety Snapshot (Quick Reference)</h2>
                                <p className="blog-block-paragraph">{queerLandscape.safety_snapshot}</p>
                            </>
                        )}

                        {queerLandscape.queer_joy && (
                            <>
                                <h2 className="blog-block-h2">8. Queer Joy, Culture & Resilience</h2>
                                <p className="blog-block-paragraph">{queerLandscape.queer_joy}</p>
                            </>
                        )}

                        {queerLandscape.final_perspective && (
                            <>
                                <h2 className="blog-block-h2">9. Final Perspective: What It Means to Be Queer Here</h2>
                                <p className="blog-block-paragraph">{queerLandscape.final_perspective}</p>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Lightbox Overlay */}
            {lightboxOpen && lightboxImages.length > 0 && (
                <div className="lightbox-backdrop" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
                        {lightboxImages.length > 1 && (
                            <>
                                <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
                                <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
                            </>
                        )}
                        <img src={lightboxImages[lightboxIndex]} alt="" className="lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
}
